/// <reference types="cookiebot-sdk" />

/* eslint-disable import/no-extraneous-dependencies */
import 'normalize.css/normalize.css';
import 'react-mosaic-component/react-mosaic-component.css';
import 'src/styles/index.scss';

import { FocusStyleManager, HotkeysProvider } from '@blueprintjs/core';
import * as immer from 'immer';
import * as React from 'react';
import { DndProvider } from 'react-dnd-multi-backend';
import HTML5toTouch from 'react-dnd-multi-backend/dist/cjs/HTML5toTouch';
import * as ReactDOM from 'react-dom';
import { QueryClientProvider } from 'react-query';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { PersistGate } from 'redux-persist/integration/react';
import {
  cookiebotAgent,
  isCypress,
  isDevelopment,
  isTesting,
} from 'vet-bones/utils';
import { checkPortalCookies } from 'vet-bones/utils/portal';

import { ErrorBoundary } from 'src/components/ErrorBoundary';
import { App } from 'src/containers/App';
import { Diagram } from 'src/containers/ProjectDiagram/types/diagram';
import {
  getPersistor,
  getStore,
  resetStore,
  setupStore,
} from 'src/store/store';
import { ReduxStore } from 'src/store/types';
import { queryClient } from 'src/utils/queryClient';

FocusStyleManager.onlyShowFocusOnTabs();

const isDevEnv = isDevelopment();
const isTestEnv = isTesting();

declare global {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface Window {
    restart(): void;
    store?: ReduxStore; // exposed only in development and testing
    diagram?: Diagram; // exposed only in development and testing
    APP_VERSION: string;
  }
}

setupStore();
const store = getStore();

window.APP_VERSION = process.env.APP_VERSION || 'unknown';

window.restart = () => {
  resetStore();
  window.location.reload();
};

if (isDevEnv || isTestEnv) {
  window.store = store;
}

if (isCypress()) {
  // Allow state mutations in integration tests
  immer.setAutoFreeze(false);
}

checkPortalCookies();

if (!isCypress()) {
  cookiebotAgent.load();
}

// use the pathname up to the hash as our basename to be able to
// treat the entire sub-path up to our index.html as our base route
//
// i.e. for `designer.stardog.com/unstable/index.html#/search...`
// our router should use a basename of `/unstable/index.html#`
// so a link to the `/` path does not navigate to `designer.stardog.com`
// but rather to `designer.stardog.com/unstable/index.html#`
const basename = `${window.location.pathname}#`;
if (!window.location.hash) {
  window.history.replaceState(null, '', `${window.location.href}#/`);
}

ReactDOM.render(
  <ErrorBoundary>
    <Provider store={store}>
      <PersistGate loading={null} persistor={getPersistor()}>
        <BrowserRouter basename={basename}>
          <QueryClientProvider client={queryClient}>
            <DndProvider options={HTML5toTouch}>
              <HotkeysProvider>
                <App />
              </HotkeysProvider>
            </DndProvider>
          </QueryClientProvider>
        </BrowserRouter>
      </PersistGate>
    </Provider>
  </ErrorBoundary>,

  document.getElementById('app-main')
);
